import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Car, Plane, MapPin, User, Clock, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { getErrorMessage } from '@/lib/errorUtils';

import { API } from '@/config/api';

const TransfersPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [transfers, setTransfers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [guests, setGuests] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);
  const [showDriverDialog, setShowDriverDialog] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState(null);
  
  const [transferForm, setTransferForm] = useState({
    reservation_id: '',
    guest_id: '',
    transfer_type: 'airport_pickup',
    pickup_location: '',
    dropoff_location: '',
    pickup_datetime: '',
    num_passengers: 1,
    notes: '',
    vehicle_id: '',
    driver_id: '',
    price: 0
  });

  const [vehicleForm, setVehicleForm] = useState({
    type: 'sedan',
    plate_number: '',
    capacity: 4,
    notes: ''
  });

  const [driverForm, setDriverForm] = useState({
    name: '',
    phone: '',
    license_number: ''
  });

  useEffect(() => {
    fetchData();
  }, [user?.hotel_id]);

  const fetchData = async () => {
    if (!user?.hotel_id) return;
    setLoading(true);
    try {
      const [transferRes, vehicleRes, driverRes, guestRes, resRes] = await Promise.all([
        axios.get(`${API}/hotels/${user.hotel_id}/transfers`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hotels/${user.hotel_id}/vehicles`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hotels/${user.hotel_id}/drivers`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hotels/${user.hotel_id}/guests`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hotels/${user.hotel_id}/reservations`, { headers: getAuthHeaders() })
      ]);
      setTransfers(transferRes.data);
      setVehicles(vehicleRes.data);
      setDrivers(driverRes.data);
      setGuests(guestRes.data);
      setReservations(resRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTransfer = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        guest_id: transferForm.guest_id,
        reservation_id: transferForm.reservation_id || null,
        transfer_type: transferForm.transfer_type,
        pickup_location: transferForm.pickup_location,
        dropoff_location: transferForm.dropoff_location,
        pickup_datetime: new Date(transferForm.pickup_datetime).toISOString(),
        num_passengers: parseInt(transferForm.num_passengers) || 1,
        notes: transferForm.notes || null,
        vehicle_id: transferForm.vehicle_id || null,
        driver_id: transferForm.driver_id || null,
        price: parseFloat(transferForm.price) || 0
      };
      
      if (editingTransfer) {
        await axios.put(
          `${API}/hotels/${user.hotel_id}/transfers/${editingTransfer.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        setTransfers(prev => prev.map(t => t.id === editingTransfer.id ? { ...t, ...payload } : t));
        toast.success('Transfer updated successfully');
      } else {
        const response = await axios.post(
          `${API}/hotels/${user.hotel_id}/transfers`,
          payload,
          { headers: getAuthHeaders() }
        );
        setTransfers(prev => [response.data, ...prev]);
        toast.success('Transfer scheduled successfully');
      }
      
      setShowTransferDialog(false);
      resetTransferForm();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save transfer'));
    }
  };

  const handleEditTransfer = (transfer) => {
    setEditingTransfer(transfer);
    setTransferForm({
      reservation_id: transfer.reservation_id || '',
      guest_id: transfer.guest_id,
      transfer_type: transfer.transfer_type,
      pickup_location: transfer.pickup_location,
      dropoff_location: transfer.dropoff_location,
      pickup_datetime: transfer.pickup_datetime?.slice(0, 16) || '',
      num_passengers: transfer.num_passengers || 1,
      notes: transfer.notes || '',
      vehicle_id: transfer.vehicle_id || '',
      driver_id: transfer.driver_id || '',
      price: transfer.price || 0
    });
    setShowTransferDialog(true);
  };

  const handleDeleteTransfer = async (transferId) => {
    if (!window.confirm('Delete this transfer?')) return;
    try {
      await axios.delete(
        `${API}/hotels/${user.hotel_id}/transfers/${transferId}`,
        { headers: getAuthHeaders() }
      );
      setTransfers(prev => prev.filter(t => t.id !== transferId));
      toast.success('Transfer deleted');
    } catch (error) {
      toast.error('Failed to delete transfer');
    }
  };

  const handleCreateVehicle = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/hotels/${user.hotel_id}/vehicles`,
        vehicleForm,
        { headers: getAuthHeaders() }
      );
      
      setVehicles(prev => [...prev, response.data]);
      setShowVehicleDialog(false);
      setVehicleForm({ type: 'sedan', plate_number: '', capacity: 4, notes: '' });
      toast.success('Vehicle added');
    } catch (error) {
      toast.error('Failed to add vehicle');
    }
  };

  const handleCreateDriver = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/hotels/${user.hotel_id}/drivers`,
        driverForm,
        { headers: getAuthHeaders() }
      );
      
      setDrivers(prev => [...prev, response.data]);
      setShowDriverDialog(false);
      setDriverForm({ name: '', phone: '', license_number: '' });
      toast.success('Driver added');
    } catch (error) {
      toast.error('Failed to add driver');
    }
  };

  const handleUpdateStatus = async (transferId, status) => {
    try {
      await axios.put(
        `${API}/hotels/${user.hotel_id}/transfers/${transferId}?status=${status}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      setTransfers(prev => prev.map(t => 
        t.id === transferId ? { ...t, status } : t
      ));
      toast.success('Transfer status updated');
    } catch (error) {
      toast.error('Failed to update transfer');
    }
  };

  const resetTransferForm = () => {
    setTransferForm({
      reservation_id: '',
      guest_id: '',
      transfer_type: 'airport_pickup',
      pickup_location: '',
      dropoff_location: '',
      pickup_datetime: '',
      num_passengers: 1,
      notes: '',
      vehicle_id: '',
      driver_id: '',
      price: 0
    });
    setEditingTransfer(null);
  };

  const getGuestName = (guestId) => {
    const guest = guests.find(g => g.id === guestId);
    return guest ? `${guest.first_name} ${guest.last_name}` : 'Unknown';
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver?.name || 'Unassigned';
  };

  const getVehicleInfo = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    return vehicle ? `${vehicle.type} (${vehicle.plate_number})` : 'Unassigned';
  };

  const getTransferTypeBadge = (type) => {
    const labels = {
      airport_pickup: 'Airport Pickup',
      airport_dropoff: 'Airport Drop-off',
      hotel_city: 'Hotel ↔ City',
      custom: 'Custom'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-700',
      confirmed: 'bg-cyan-100 text-cyan-700',
      in_progress: 'bg-amber-100 text-amber-700',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-rose-100 text-rose-700',
      no_show: 'bg-slate-100 text-slate-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const todaysTransfers = transfers.filter(t => 
    t.pickup_datetime?.slice(0, 10) === format(new Date(), 'yyyy-MM-dd')
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="transfers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-['Outfit']">Transfers</h1>
          <p className="text-sm text-muted-foreground">Manage guest transportation</p>
        </div>
        <Dialog open={showTransferDialog} onOpenChange={(open) => { setShowTransferDialog(open); if (!open) resetTransferForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="schedule-transfer-btn" disabled={guests.length === 0} size="sm" className="sm:size-default">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Schedule Transfer</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg mx-2 sm:mx-auto">
            <DialogHeader>
              <DialogTitle>{editingTransfer ? 'Edit Transfer' : 'Schedule Transfer'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTransfer} className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="space-y-2">
                <Label>Guest *</Label>
                <Select
                  value={transferForm.guest_id}
                  onValueChange={(value) => setTransferForm({ ...transferForm, guest_id: value })}
                >
                  <SelectTrigger data-testid="transfer-guest-select">
                    <SelectValue placeholder="Select guest" />
                  </SelectTrigger>
                  <SelectContent>
                    {guests.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.first_name} {g.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Transfer Type</Label>
                <Select
                  value={transferForm.transfer_type}
                  onValueChange={(value) => setTransferForm({ ...transferForm, transfer_type: value })}
                >
                  <SelectTrigger data-testid="transfer-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="airport_pickup">Airport Pickup</SelectItem>
                    <SelectItem value="airport_dropoff">Airport Drop-off</SelectItem>
                    <SelectItem value="hotel_city">Hotel ↔ City</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Pickup Location *</Label>
                  <Input
                    value={transferForm.pickup_location}
                    onChange={(e) => setTransferForm({ ...transferForm, pickup_location: e.target.value })}
                    placeholder="Airport Terminal 2"
                    data-testid="transfer-pickup-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Drop-off Location *</Label>
                  <Input
                    value={transferForm.dropoff_location}
                    onChange={(e) => setTransferForm({ ...transferForm, dropoff_location: e.target.value })}
                    placeholder="Hotel Lobby"
                    data-testid="transfer-dropoff-input"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date & Time *</Label>
                  <Input
                    type="datetime-local"
                    value={transferForm.pickup_datetime}
                    onChange={(e) => setTransferForm({ ...transferForm, pickup_datetime: e.target.value })}
                    data-testid="transfer-datetime-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Passengers</Label>
                  <Input
                    type="number"
                    min="1"
                    value={transferForm.num_passengers}
                    onChange={(e) => setTransferForm({ ...transferForm, num_passengers: parseInt(e.target.value) })}
                    data-testid="transfer-passengers-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vehicle</Label>
                  <Select
                    value={transferForm.vehicle_id}
                    onValueChange={(value) => setTransferForm({ ...transferForm, vehicle_id: value })}
                  >
                    <SelectTrigger data-testid="transfer-vehicle-select">
                      <SelectValue placeholder="Select vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicles.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.type} - {v.plate_number}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Driver</Label>
                  <Select
                    value={transferForm.driver_id}
                    onValueChange={(value) => setTransferForm({ ...transferForm, driver_id: value })}
                  >
                    <SelectTrigger data-testid="transfer-driver-select">
                      <SelectValue placeholder="Select driver" />
                    </SelectTrigger>
                    <SelectContent>
                      {drivers.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Price (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={transferForm.price}
                  onChange={(e) => setTransferForm({ ...transferForm, price: e.target.value })}
                  placeholder="50.00"
                  data-testid="transfer-price-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={transferForm.notes}
                  onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                  placeholder="2 large suitcases, 1 carry-on"
                  data-testid="transfer-notes-input"
                />
              </div>

              <Button type="submit" className="w-full" data-testid="submit-transfer-btn">
                {editingTransfer ? 'Update Transfer' : 'Schedule Transfer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today's Transfers</p>
                <p className="text-2xl font-bold">{todaysTransfers.length}</p>
              </div>
              <Car className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Vehicles</p>
                <p className="text-2xl font-bold">{vehicles.length}</p>
              </div>
              <Car className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Drivers</p>
                <p className="text-2xl font-bold">{drivers.length}</p>
              </div>
              <User className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">All Transfers</p>
                <p className="text-2xl font-bold">{transfers.length}</p>
              </div>
              <Plane className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="transfers" className="space-y-4">
        <TabsList>
          <TabsTrigger value="transfers">Transfers</TabsTrigger>
          <TabsTrigger value="vehicles">Vehicles</TabsTrigger>
          <TabsTrigger value="drivers">Drivers</TabsTrigger>
        </TabsList>

        <TabsContent value="transfers">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Guest</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Route</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date/Time</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Driver</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transfers.map((transfer) => (
                      <tr key={transfer.id} className="border-b hover:bg-muted/30" data-testid={`transfer-row-${transfer.id}`}>
                        <td className="py-3 px-4 font-medium">{getGuestName(transfer.guest_id)}</td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{getTransferTypeBadge(transfer.transfer_type)}</Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1 text-sm">
                            <MapPin className="h-3 w-3" />
                            {transfer.pickup_location} → {transfer.dropoff_location}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {transfer.pickup_datetime?.slice(0, 16).replace('T', ' ')}
                          </div>
                        </td>
                        <td className="py-3 px-4">{getDriverName(transfer.driver_id)}</td>
                        <td className="py-3 px-4">
                          <Badge className={getStatusBadge(transfer.status)}>
                            {transfer.status?.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            {transfer.status === 'scheduled' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateStatus(transfer.id, 'confirmed')}
                              >
                                Confirm
                              </Button>
                            )}
                            {transfer.status === 'confirmed' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateStatus(transfer.id, 'in_progress')}
                              >
                                Start
                              </Button>
                            )}
                            {transfer.status === 'in_progress' && (
                              <Button
                                size="sm"
                                onClick={() => handleUpdateStatus(transfer.id, 'completed')}
                              >
                                Complete
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditTransfer(transfer)}
                              data-testid={`edit-transfer-${transfer.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteTransfer(transfer.id)}
                              data-testid={`delete-transfer-${transfer.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {transfers.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No transfers scheduled.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vehicles">
          <div className="flex justify-end mb-4">
            <Dialog open={showVehicleDialog} onOpenChange={setShowVehicleDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-vehicle-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Vehicle
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Vehicle</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateVehicle} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Vehicle Type</Label>
                    <Select
                      value={vehicleForm.type}
                      onValueChange={(value) => setVehicleForm({ ...vehicleForm, type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sedan">Sedan</SelectItem>
                        <SelectItem value="suv">SUV</SelectItem>
                        <SelectItem value="van">Van</SelectItem>
                        <SelectItem value="bus">Bus</SelectItem>
                        <SelectItem value="luxury">Luxury</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Plate Number *</Label>
                    <Input
                      value={vehicleForm.plate_number}
                      onChange={(e) => setVehicleForm({ ...vehicleForm, plate_number: e.target.value })}
                      placeholder="AB-123-CD"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Capacity</Label>
                    <Input
                      type="number"
                      value={vehicleForm.capacity}
                      onChange={(e) => setVehicleForm({ ...vehicleForm, capacity: parseInt(e.target.value) })}
                    />
                  </div>
                  <Button type="submit" className="w-full">Add Vehicle</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {vehicles.map((v) => (
              <Card key={v.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Car className="h-8 w-8 text-blue-500" />
                    <div>
                      <p className="font-medium capitalize">{v.type}</p>
                      <p className="text-sm text-muted-foreground">{v.plate_number}</p>
                      <p className="text-sm text-muted-foreground">{v.capacity} passengers</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {vehicles.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No vehicles added yet.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="drivers">
          <div className="flex justify-end mb-4">
            <Dialog open={showDriverDialog} onOpenChange={setShowDriverDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-driver-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Driver
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Driver</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateDriver} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={driverForm.name}
                      onChange={(e) => setDriverForm({ ...driverForm, name: e.target.value })}
                      placeholder="Jean Dupont"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input
                      value={driverForm.phone}
                      onChange={(e) => setDriverForm({ ...driverForm, phone: e.target.value })}
                      placeholder="+33 6 12 34 56 78"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>License Number</Label>
                    <Input
                      value={driverForm.license_number}
                      onChange={(e) => setDriverForm({ ...driverForm, license_number: e.target.value })}
                      placeholder="AB12345678"
                    />
                  </div>
                  <Button type="submit" className="w-full">Add Driver</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {drivers.map((d) => (
              <Card key={d.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium">{d.name}</p>
                      <p className="text-sm text-muted-foreground">{d.phone}</p>
                      <Badge variant={d.is_available ? 'default' : 'secondary'} className="mt-1">
                        {d.is_available ? 'Available' : 'Busy'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {drivers.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No drivers added yet.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TransfersPage;
